import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search, Tag, Trash2, UserPlus, X } from 'lucide-react';
import { Alert, Button, Checkbox, Dialog, Field, Input, Spinner, Textarea, cn, useToast } from '@shripos/ui';
import { api } from '../../lib/api.js';
import { rupees, toPaise, toRupeeInput } from '../../lib/money.js';
import { CollectDueDialog } from '../customers/CollectDueDialog.jsx';
import { searchCachedCustomers } from '../../lib/offline/cache.js';
import { isNetworkError, useOffline } from '../../stores/offline.js';
import { useCart } from '../../stores/cart.js';
import { kitchenOf, useSession } from '../../stores/session.js';

/** Percent / flat discount editor. value: { type, valueBp | valuePaise } | null */
function DiscountEditor({ value, onChange, limitPct }) {
  const { t } = useTranslation();
  const [type, setType] = useState(value?.type ?? 'percent');
  const [text, setText] = useState(
    value ? (value.type === 'percent' ? String(value.valueBp / 100) : toRupeeInput(value.valuePaise)) : '',
  );
  const emit = (ty, tx) => {
    const n = Number(tx);
    if (!tx || !Number.isFinite(n) || n <= 0) return onChange(null);
    onChange(
      ty === 'percent'
        ? { type: 'percent', valueBp: Math.round(Math.min(n, 100) * 100) }
        : { type: 'flat', valuePaise: toPaise(tx) },
    );
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex rounded-lg ring-1 ring-slate-300">
          {[
            ['percent', '%'],
            ['flat', '₹'],
          ].map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setType(k);
                emit(k, text);
              }}
              className={cn('w-12 py-2 font-semibold', type === k ? 'bg-slate-900 text-white' : 'text-slate-600')}
            >
              {l}
            </button>
          ))}
        </div>
        <Input
          inputMode="decimal"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            emit(type, e.target.value);
          }}
          placeholder={t('pos.example', 'e.g. {{value}}', { value: type === 'percent' ? 10 : 20 })}
        />
      </div>
      {type === 'percent' && (
        <div className="flex gap-2">
          {[5, 10, 15, 20].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setText(String(p));
                emit('percent', String(p));
              }}
              className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300 hover:bg-slate-50"
            >
              {p}%
            </button>
          ))}
        </div>
      )}
      {limitPct != null && (
        <p className="text-xs text-slate-500">
          {t('pos.discountLimit', 'Your limit: {{pct}}%. Larger discounts need a manager.', { pct: limitPct })}
        </p>
      )}
    </div>
  );
}

export function LineDialog({ line, meta, onClose, limits }) {
  const { t } = useTranslation();
  const kitchenOrders = useSession((st) => kitchenOf(st.me).orders);
  const updateLine = useCart((s) => s.updateLine);
  const removeLine = useCart((s) => s.removeLine);
  const [note, setNote] = useState(line.note ?? '');
  const [discount, setDiscount] = useState(line.discount ?? null);
  return (
    <Dialog
      open
      onClose={onClose}
      title={meta?.name ?? t('pos.item', 'Item')}
      size="sm"
      footer={
        <>
          <Button
            variant="danger-outline"
            icon={Trash2}
            onClick={() => {
              removeLine(line.lineId);
              onClose();
            }}
          >
            {t('pos.remove', 'Remove')}
          </Button>
          <Button
            onClick={() => {
              updateLine(line.lineId, { note: note.trim(), discount });
              onClose();
            }}
          >
            {t('pos.done', 'Done')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={kitchenOrders ? t('pos.kitchenNote', 'Note for the kitchen') : t('pos.lineNote', 'Note')}>
          <Input value={note} onChange={(e) => setNote(e.target.value.slice(0, 140))} />
        </Field>
        <Field label={t('pos.itemDiscount', 'Item discount')}>
          <DiscountEditor value={discount} onChange={setDiscount} limitPct={limits?.maxDiscountPct} />
        </Field>
      </div>
    </Dialog>
  );
}

export function OffersDialog({ onClose, limits, onCoupon }) {
  const { t } = useTranslation();
  const cart = useCart((s) => s.cart);
  const patch = useCart((s) => s.patch);
  const [discount, setDiscount] = useState(cart.billDiscount);
  const [reason, setReason] = useState(cart.billDiscount?.reason ?? '');
  const [code, setCode] = useState(cart.couponCode ?? '');
  const [checking, setChecking] = useState(false);
  const [couponError, setCouponError] = useState(null);

  const apply = async () => {
    setCouponError(null);
    let coupon = null;
    if (code.trim()) {
      setChecking(true);
      try {
        coupon = await api.get(`/pos/coupons/${encodeURIComponent(code.trim())}`);
      } catch (err) {
        setCouponError(err.fields?.couponCode ?? err.message);
        setChecking(false);
        return;
      }
      setChecking(false);
    }
    patch({ billDiscount: discount ? { ...discount, reason: reason.trim() } : null, couponCode: coupon?.code ?? '' });
    onCoupon(coupon);
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('pos.offersTitle', 'Discount & coupon')}
      size="sm"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              patch({ billDiscount: null, couponCode: '' });
              onCoupon(null);
              onClose();
            }}
          >
            {t('pos.clearAll', 'Clear all')}
          </Button>
          <Button onClick={apply} loading={checking}>
            {t('pos.apply', 'Apply')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field label={t('pos.billDiscount', 'Bill discount')}>
          <DiscountEditor value={discount} onChange={setDiscount} limitPct={limits?.maxDiscountPct} />
        </Field>
        {discount && (
          <Field label={t('pos.reasonOptional', 'Reason (optional)')}>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 120))}
              placeholder={t('pos.reasonExample', 'e.g. regular customer')}
            />
          </Field>
        )}
        <Field label={t('pos.couponCode', 'Coupon code')} error={couponError}>
          <div className="relative">
            <Tag className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="pl-9 font-mono uppercase"
              placeholder="WELCOME10"
            />
          </div>
        </Field>
      </div>
    </Dialog>
  );
}

export function CustomerDialog({ onClose }) {
  const { t } = useTranslation();
  const toast = useToast();
  const cart = useCart((s) => s.cart);
  const patch = useCart((s) => s.patch);
  const [phone, setPhone] = useState(cart.customer?.phone ?? '');
  const [creating, setCreating] = useState(false);
  const [collecting, setCollecting] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', gstin: '', marketingConsent: false });
  const [error, setError] = useState(null);
  const digits = phone.replace(/\D/g, '');
  const results = useQuery({
    queryKey: ['customers', digits],
    // Offline: search the recent customers kept on this device.
    queryFn: () =>
      api.get(`/customers?phone=${digits}`).catch(async (err) => {
        if (!isNetworkError(err)) throw err;
        return { items: await searchCachedCustomers(digits), offline: true };
      }),
    enabled: digits.length >= 3,
    networkMode: 'always',
  });

  const pick = (c) => {
    patch({
      customer: {
        id: c.id,
        name: c.name,
        phone: c.phone,
        gstin: c.gstin,
        points: c.points ?? 0,
        duePaise: c.duePaise ?? 0,
      },
    });
    onClose();
  };

  const create = async () => {
    setError(null);
    if (!useOffline.getState().reachable) {
      // Added on the server when the bill syncs (Settings → Offline & displays).
      if (digits.length < 10) return setError(t('pos.fullMobile', 'Enter the full mobile number'));
      patch({
        customer: {
          id: null,
          offlineNew: true,
          phone: digits.slice(-10),
          name: form.name || null,
          points: 0,
          duePaise: 0,
        },
      });
      onClose();
      return;
    }
    try {
      const c = await api.post('/customers', { phone, ...form });
      toast.success(t('pos.customerAdded', 'Customer added'));
      pick(c);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('pos.customer', 'Customer')}
      size="sm"
      footer={
        cart.customer && (
          <Button
            variant="ghost"
            icon={X}
            onClick={() => {
              patch({ customer: null });
              onClose();
            }}
          >
            {t('pos.removeCustomer', 'Remove customer')}
          </Button>
        )
      }
    >
      <div className="space-y-4">
        <Field label={t('pos.mobile', 'Mobile number')}>
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              autoFocus
              inputMode="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setCreating(false);
              }}
              className="pl-9"
              aria-label={t('pos.mobile', 'Mobile number')}
              placeholder="98480 22338"
              size="lg"
            />
          </div>
        </Field>
        {results.isFetching && <Spinner />}
        {!creating && results.data?.items.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
            {results.data.items.map((c) => (
              <li key={c.id} className="flex items-center">
                <button
                  onClick={() => pick(c)}
                  className="flex min-w-0 flex-1 items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50"
                >
                  <span>
                    <span className="block font-medium">{c.name || t('pos.noName', 'No name')}</span>
                    <span className="text-xs text-slate-500">
                      {c.phone} · {t('pos.visits', '{{count}} visits', { count: c.visits ?? 0 })}
                      {c.points > 0 ? ` · ${t('pos.pointsShort', '{{count}} pts', { count: c.points })}` : ''}
                      {c.duePaise > 0
                        ? ` · ${t('pos.dueShort', 'due {{amount}}', { amount: rupees(c.duePaise) })}`
                        : ''}
                    </span>
                  </span>
                </button>
                {c.duePaise > 0 && (
                  <Button size="sm" variant="secondary" className="mr-2 shrink-0" onClick={() => setCollecting(c)}>
                    {t('pos.collectDue', 'Collect due')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <CollectDueDialog
          customer={collecting}
          open={Boolean(collecting)}
          onClose={() => setCollecting(null)}
          onDone={(r) => {
            toast.success(
              r.dueAfterPaise
                ? t('pos.stillOwes', 'Payment recorded. Still owes {{amount}}', { amount: rupees(r.dueAfterPaise) })
                : t('pos.duesCleared', 'All dues cleared'),
            );
            results.refetch();
          }}
        />
        {!creating &&
          digits.length >= 10 &&
          !results.isFetching &&
          !results.data?.items.some((c) => c.phone.endsWith(digits.slice(-10))) && (
            <Button variant="secondary" icon={UserPlus} className="w-full" onClick={() => setCreating(true)}>
              {t('pos.addNewCustomer', 'Add new customer')}
            </Button>
          )}
        {creating && (
          <div className="space-y-3 rounded-lg bg-slate-50 p-3">
            {error && <Alert tone="danger">{error}</Alert>}
            <Field label={t('pos.name', 'Name')}>
              <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label={t('pos.emailOptional', 'Email (optional)')}>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label={t('pos.gstinBusiness', 'GSTIN (business customers)')}>
              <Input
                className="uppercase"
                value={form.gstin}
                onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
              />
            </Field>
            <Checkbox
              label={t('pos.consent', 'Customer agrees to receive offers by SMS/WhatsApp')}
              description={t('pos.consentNote', "Recorded with today's date (DPDP Act consent).")}
              checked={form.marketingConsent}
              onChange={(e) => setForm({ ...form, marketingConsent: e.target.checked })}
            />
            <Button className="w-full" onClick={create}>
              {t('pos.saveAttach', 'Save & attach')}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export function ReasonDialog({ title, description, confirmLabel, tone = 'danger', onConfirm, onClose }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.back', 'Back')}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm(reason.trim());
                onClose();
              } catch (err) {
                if (!err?.cancelled) setError(err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel ?? t('pos.confirm', 'Confirm')}
          </Button>
        </>
      }
    >
      {error && (
        <Alert tone="danger" className="mb-3">
          {error}
        </Alert>
      )}
      <Field label={t('pos.reasonRequired', 'Reason (required)')}>
        <Textarea autoFocus rows={2} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 300))} />
      </Field>
    </Dialog>
  );
}
